import React, { useState, useEffect } from 'react';
import { getLocations, type ComparisonLocation } from '../../../api/comparisonApi';

const LOCATION_FLAGS: Record<string, string> = {
  // Countries by country_code
  US: '🇺🇸',
  GB: '🇬🇧',
  CA: '🇨🇦',
  AU: '🇦🇺',
  DE: '🇩🇪',
  FR: '🇫🇷',
  NL: '🇳🇱',
  IN: '🇮🇳',
  SG: '🇸🇬',
  JP: '🇯🇵',
  PL: '🇵🇱',
  ES: '🇪🇸',
  IT: '🇮🇹',
  SE: '🇸🇪',
  NO: '🇳🇴',
  DK: '🇩🇰',
  FI: '🇫🇮',
  BR: '🇧🇷',
  MX: '🇲🇽',
  ZA: '🇿🇦',
  AE: '🇦🇪',
  NZ: '🇳🇿',
  IE: '🇮🇪',
};

interface LocationPickerProps {
  value: string | null;
  onChange: (locationId: string, locationName: string) => void;
  disabled?: boolean;
}

function getLocationLabel(location: ComparisonLocation): string {
  if (location.type === 'country') return location.name;
  if (location.type === 'state') return location.name;
  return `${location.name}, ${location.region_code || location.country_code}`;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [locations, setLocations] = useState<ComparisonLocation[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<ComparisonLocation[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoading(true);
        const list = await getLocations();
        setLocations(list);
        setFilteredLocations(list);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    loadLocations();
  }, []);

  useEffect(() => {
    const searchLower = searchTerm.toLowerCase();
    const filtered = locations.filter(
      (location) =>
        location.type === 'country' &&
        (location.name.toLowerCase().includes(searchLower) ||
          (location.country_code?.toLowerCase().includes(searchLower) ?? false))
    );

    filtered.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredLocations(filtered);
  }, [searchTerm, locations]);

  if (loading) return <p className="text-sm text-gray-500">Loading locations...</p>;
  if (error) return <p className="text-sm text-red-600">Error: {error}</p>;

  // Group locations
  const grouped = filteredLocations.reduce(
    (acc, loc) => {
      const g = loc.group || 'Other';
      if (!acc[g]) acc[g] = [];
      acc[g].push(loc);
      return acc;
    },
    {} as Record<string, ComparisonLocation[]>
  );

  return (
    <div>
      <input
        type="text"
        placeholder="Search locations…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={disabled}
        className="w-full max-w-md px-4 py-3 rounded-xl border border-gray-200 text-sm text-secondary outline-none mb-6 bg-white transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
      />

      {filteredLocations.length === 0 && (
        <p className="text-sm text-gray-500">No locations match your search.</p>
      )}

      {Object.entries(grouped).map(([groupName, items]) => (
        <div key={groupName} className="mb-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
            {groupName}
          </p>
          <div className="flex flex-wrap gap-2">
            {items.map((location) => {
              const active = value === location.id;
              const label = getLocationLabel(location);
              return (
                <button
                  key={location.id}
                  onClick={() => !disabled && onChange(location.id, label)}
                  disabled={disabled}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    active
                      ? 'border-primary bg-red-50 text-primary'
                      : 'border-gray-200 bg-white text-secondary hover:border-primary hover:bg-red-50/50'
                  }`}
                >
                  {LOCATION_FLAGS[location.country_code] && (
                    <span>{LOCATION_FLAGS[location.country_code]}</span>
                  )}
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
